import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchCollection, fetchTaxonomyTerms, getVal } from '../lib/api';

export default function Maskiner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'alle';

  const [machines, setMachines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    async function loadCategories() {
      try {
        const terms = await fetchTaxonomyTerms('category');
        setCategories(terms || []);
      } catch (err) {
        console.error('Fejl ved hentning af kategorier:', err);
      } finally {
        setCategoriesLoading(false);
      }
    }
    loadCategories();
  }, []);

  useEffect(() => {
    async function loadMachines() {
      setLoading(true);
      try {
        const params = {};
        if (activeCategory !== 'alle') {
          params.term = `category:${activeCategory}`;
        }
        const data = await fetchCollection('maskiner', params);
        setMachines(data.items || []);
      } catch (err) {
        console.error('Fejl ved hentning af maskiner:', err);
      } finally {
        setLoading(false);
      }
    }
    loadMachines();
  }, [activeCategory]);

  const handleCategoryChange = (slug) => {
    if (slug === 'alle') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', slug);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="flex min-h-screen flex-col bg-stone-50">
      <section className="border-b border-stone-200 bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.35em] text-brand-orange">Vores maskinpark</span>
          <h1 className="mt-3 text-4xl font-semibold text-slate-800 sm:text-5xl">Udlejningsudstyr & maskiner</h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
            Se vores fulde udvalg af professionelle maskiner og festudstyr. Alt vores udstyr er velholdt, godkendt og klar til brug.
          </p>
        </div>
      </section>

      <section className="flex-grow py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h3 className="mb-4 text-center text-sm font-bold uppercase tracking-[0.35em] text-slate-500 sm:text-left">
              Filtrer efter kategori:
            </h3>
            {categoriesLoading ? (
              <div className="h-10 animate-pulse rounded-2xl bg-stone-200" />
            ) : (
              <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                <button
                  onClick={() => handleCategoryChange('alle')}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${activeCategory === 'alle' ? 'bg-brand-orange text-white' : 'border border-stone-200 bg-white text-slate-600 hover:border-brand-orange hover:text-brand-orange'}`}
                >
                  Alle produkter
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${activeCategory === cat.slug ? 'bg-brand-orange text-white' : 'border border-stone-200 bg-white text-slate-600 hover:border-brand-orange hover:text-brand-orange'}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
            </div>
          ) : machines.length === 0 ? (
            <div className="mx-auto max-w-xl rounded-[1.5rem] border border-stone-200 bg-white p-8 text-center shadow-[0_12px_30px_rgba(148,163,184,0.08)]">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-stone-100 text-stone-400">
                <i className="fa-solid fa-triangle-exclamation text-2xl" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-slate-800">Ingen produkter fundet</h3>
              <p className="mx-auto max-w-md text-slate-500">
                Der er i øjeblikket ingen produkter i denne kategori. Prøv at vælge en anden kategori eller kontakt os for forespørgsler.
              </p>
              <button
                onClick={() => handleCategoryChange('alle')}
                className="mt-6 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-orange-hover"
              >
                Vis alle produkter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {machines.map((machine) => (
                <div key={machine.id} className="overflow-hidden rounded-[1.5rem] border border-stone-200 bg-white shadow-[0_12px_30px_rgba(148,163,184,0.08)] transition-all duration-300 hover:border-brand-orange/30">
                  <div className="relative h-56 overflow-hidden bg-stone-100">
                    <img
                      src={machine.cover_image?.[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${machine.cover_image[0]}` : ''}
                      alt={getVal(machine.name)}
                      className="h-full w-full object-cover object-center transition-transform duration-300 hover:scale-105"
                    />
                    <div className="absolute right-4 top-4 rounded-full bg-brand-orange px-3 py-1 text-xs font-bold text-white shadow-lg shadow-brand-orange/15">
                      {getVal(machine.price)}
                    </div>
                  </div>

                  <div className="flex flex-grow flex-col p-6">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-brand-orange" />
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                        {machine.terms?.category?.[0] ? machine.terms.category[0].replace('-', ' ') : 'Udlejning'}
                      </span>
                    </div>
                    <h2 className="mb-3 text-xl font-semibold text-slate-800">{getVal(machine.name)}</h2>
                    <div
                      className="mb-6 text-sm leading-relaxed text-slate-500 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: getVal(machine.description) }}
                    />
                    <div className="mt-auto flex items-center justify-between border-t border-stone-200 pt-4">
                      <span className="text-sm font-bold text-slate-800">{getVal(machine.price)}</span>
                      <Link
                        to={`/maskiner/${machine.id}`}
                        className="inline-flex items-center gap-2 text-sm font-bold text-brand-orange transition-colors hover:text-brand-orange-hover"
                      >
                        Se detaljer <i className="fa-solid fa-chevron-right text-xs" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
